import { useState, useCallback, useEffect } from 'react'

interface UseReactionsPickerProps {
  messageId: string
}

interface UseReactionsPickerResult {
  isReactionPickerVisible: boolean
  isFullPickerOpen: boolean
  handleReactionButtonClick: (e: React.MouseEvent) => void
  handleEmojiSelect: (emoji: string) => void
  openFullPicker: () => void
}

/**
 * Hook for managing state and behavior of emoji reaction picker
 * @param messageId - Unique identifier of the message/element to which the picker is attached
 */
export const useReactionsPicker = ({ messageId }: UseReactionsPickerProps): UseReactionsPickerResult => {
  const [isReactionPickerVisible, setIsReactionPickerVisible] = useState(false)
  const [isFullPickerOpen, setIsFullPickerOpen] = useState(false)

  // Handler for reaction button click
  const handleReactionButtonClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()

      // If picker is already open for this message, close it
      if (isReactionPickerVisible) {
        setIsReactionPickerVisible(false)
        setIsFullPickerOpen(false)
        return
      }

      // First close all other pickers
      const event = new CustomEvent('closeAllReactionPickers', {
        detail: { messageId },
      })
      window.dispatchEvent(event)

      // Then open picker for current message
      setTimeout(() => {
        setIsReactionPickerVisible(true)
      }, 0)
    },
    [isReactionPickerVisible, messageId]
  )

  // Handler for emoji selection
  const handleEmojiSelect = useCallback(() => {
    // Close picker immediately after emoji selection
    setIsReactionPickerVisible(false)
    setIsFullPickerOpen(false)
  }, [])

  // Opens full emoji picker
  const openFullPicker = useCallback(() => {
    setIsFullPickerOpen(true)
  }, [])

  // Handler for closing all pickers event
  useEffect(() => {
    const handleCloseAllPickers = (e: Event) => {
      const customEvent = e as CustomEvent
      // Close picker only if it doesn't belong to current message
      if (customEvent.detail?.messageId !== messageId) {
        setIsReactionPickerVisible(false)
        setIsFullPickerOpen(false)
      }
    }

    window.addEventListener('closeAllReactionPickers', handleCloseAllPickers)
    return () => {
      window.removeEventListener('closeAllReactionPickers', handleCloseAllPickers)
    }
  }, [messageId])

  return {
    isReactionPickerVisible,
    isFullPickerOpen,
    handleReactionButtonClick,
    handleEmojiSelect,
    openFullPicker,
  }
}
